import type { CustomWorkbookDetail } from '@mathflat/domain/MakeCustomWorkbook'
import type {
  ConceptWorksheetFilterBody,
  CustomPaperWorksheetFilterBody,
  MakeWorksheetProblem,
  MergedWorksheetFilterBody,
  MyDbOriginalWorksheetFilterBody,
  MyDbProblemDetail,
  MyDbSimilarWorksheetFilterBody,
  OneClickWorksheetFilterBody,
  PracticeTestWorksheetFilterBody,
  PublicWorkbookWorksheetFilterBody,
  SchoolTestPaperWorksheetFilterBody,
  SchoolTestWorksheetFilterBody,
  WeakPeriodWorksheetFilterBody,
  WorksheetFilterDraft,
} from '@mathflat/domain/MakeWorksheet'
import type { ProblemLevel, SimilarLevel } from '@mathflat/domain/Problem'
import type { AllGradeSubject, Revision, SchoolType, Semester } from '@mathflat/domain/SchoolSystem'
import type { Student } from '@mathflat/domain/Student'
import type { WorksheetMergeProblemSortType } from '@mathflat/domain/Worksheet'
import { makeObservable, observable } from 'mobx'
import { EMPTY_NUMBER_ID } from 'src/constant/common'

import commonRepo from '@/repository/common.repo'
import type { DerivationFilter } from '@/types/derivationFilter'
import type {
  ArithmeticLevel,
  MakeWorksheetEqualityLevel,
  MakeWorksheetFilterProblemOption,
  PracticeTestOption,
  PreviousExclusionScope,
  ProblemFilterType,
  ProblemRequestType,
} from '@/types/makeWorksheet'
import type {
  ProblemSourceFilterCount,
  ProblemSourceFilterType,
  ProblemSourceType,
} from '@/types/problemSource'

import { DTO } from '../DTO'
import type {
  ArithmeticChapterFilterParams,
  LeveledChapterFilterParams,
} from './MakeWorksheetByChapterOption'
import type { Worksheet } from './Worksheet'

export class MakeWorksheetFilterOption extends DTO {
  // 공통
  type: WorksheetFilterDraft['type'] = 'CONCEPT'
  filterId = '' // 필터 고유 아이디
  practiceTest: PracticeTestOption = 'INCLUDE' // 모의고사 문제 포함여부 - include: 문제 포함, only: 문제만, exclude: 문제 제외
  onlyAutoScorable = false // 자동채점 가능여부
  excludePrevious = false // 기존 출제문제 제외여부
  previousExclusionScope?: PreviousExclusionScope // 기존 출제 문제 제외 기준
  studentIds?: Student['id'][] // 제외 기준이 STUDENT_ASSIGNED일 때 대상 학생 id 목록
  problemFilterType: ProblemFilterType = 'ALL'
  excludeOOC = false // 교육과정 외 유형 제외
  includeMyDbProblem = commonRepo.isMyDbAllowed // 본인 업로드(나의 DB) 문제 포함 여부
  studentId = ''

  problemList: MakeWorksheetProblem[] | null = null // 문제 아이디 리스트 1단계 - null 복제후 수정, 수정은 기존 학습지 문제 아이디 리스트를 가지고 있음.

  title = '' // 학습지 3단계 제목
  grade: AllGradeSubject = '1' // 학년
  semester: Semester = null // 학기
  schoolType: Student['schoolType'] | null = null // 학교급 정보
  revision: Revision = 'CURRICULUM_15' // 개정
  worksheetRange: string | null = null // 학습지 범위

  selectedConceptIdList: number[] = [] // 선택한 유형별 개념
  selectedLittleChapterIdList: number[] = [] // 선택한 소단원별 개념

  // 단원 유형별 옵션
  conceptIdList: number[] = [] // 유형 선택 아이디리스트
  excludedTopicIds: number[] = [] // 제외할 주제(유형) id list
  excludedSubTopicIds: number[] = [] // 제외할 세부(유형) id list
  problemCount = 50 // 문제 수
  level: ProblemLevel = 3 // 문제 난이도
  levelWeight: [number, number, number, number, number] = [100, 0, 0, 0, 0]
  arithmeticOnly = false // 연산 학습지 여부
  arithmeticLevel: ArithmeticLevel | null = null // 연산 난이도 3택 — 연산 필터 되읽기에만 실림
  equalityLevel: MakeWorksheetEqualityLevel = null // 균등배분시 기준 - 선택 안하면 null
  minRate = 0 // 정답률 - 최대
  maxRate = 100 // 정답률 - 최대

  // 시중교재
  problemOption: MakeWorksheetFilterProblemOption = 'ALL' // 문제 옵션 SIMILAR - 유사문제만 TWIN - 쌍둥이문제만 NULL - 둘다 포함
  pairX: 0 | 1 | 2 | 3 = 1 // 쌍둥이 문제 수
  similarX: 0 | 1 | 2 | 3 = 1 // 유사 문제 수
  workbookId: number = EMPTY_NUMBER_ID // 시중교재 아이디
  similarLevel: SimilarLevel = 'AS_IS' // 유사문제 난이도 EASIER - 더 쉽게 AS_IS - 그대로 HARDER - 더 어렵게
  limitProblemCountPerConcept: PublicWorkbookWorksheetFilterBody['limitProblemCountPerConcept'] =
    null // 유형별 최대 문제수 제한
  workbookProblemIdList: PublicWorkbookWorksheetFilterBody['workbookProblemIdList'] = [] // 선택한 문제 아이디
  // 시중교재로 만들 학습지일 경우 존재
  originWorkbook: CustomWorkbookDetail['originWorkbook'] = null
  // 수능
  practiceProblemIdList: number[] = [] // 학습지 선택 아이디리스트
  curriculumIdList: number[] = []
  schoolGradeType: 1 | 2 | 3 = 1 // 학년 (고1, 고2, 고3)
  year: number[] = [] // 대상 년도들
  department: ('LIBERAL' | 'SCIENCE')[] = [] // 문이과 - 전체면 null
  month: number[] = [] // 대상 월들
  subjectList: number[] = [] // 과목들
  scoreList: (2 | 3 | 4)[] = [2, 3, 4] // 배점 - 전체면 null

  // 그외
  academyId = '' // 기관 아이디
  timeToLive = 0 // ttl 시간단위
  source: string | undefined = undefined // origin worksheet type (보내는대로 오는 값)

  // WEAK_PERIOD
  startDate = ''
  endDate = ''

  // SCHOOL_TEST
  schoolCodes: string[] = []
  schoolTestGrade: AllGradeSubject | null = null
  schoolTestSchoolType: Exclude<SchoolType, 'ELEMENTARY'> | null = null
  schoolTestRevision: Revision | null = null
  schoolTestSemester: Semester | null = null
  schoolTestYears: SchoolTestWorksheetFilterBody['schoolTestYears'] = []
  schoolTestType: SchoolTestWorksheetFilterBody['schoolTestType'] = null // SCHOOL_TEST_PAPER 도 사용

  // SCHOOL_TEST_PAPER
  // CUSTOM_PAPER
  paperId = EMPTY_NUMBER_ID
  paperTitle = ''
  selectedPageIndexes: number[] = []
  schoolName = ''
  schoolTestYear = EMPTY_NUMBER_ID
  trieKey = ''

  // ONE_CLICK
  problemSource: {
    type: ProblemSourceType
    filterType: ProblemSourceFilterType
    filterTypeOption: {
      filterCount: ProblemSourceFilterCount
    }
  } = {
    type: 'WORKSHEET',
    filterType: 'RECENTLY_SCORED',
    filterTypeOption: {
      filterCount: 5,
    },
  }
  problemRequestType: ProblemRequestType = 'SIMILAR'
  levelType: DerivationFilter.LevelType['levelType'] = 'NORMAL'

  // MERGED_WORKSHEET
  mergedWorksheetIds: Worksheet['id'][] = []
  problemSortType: WorksheetMergeProblemSortType = 'BY_CONCEPT'

  // MY_DB_ORIGINAL / MY_DB_SIMILAR
  myDbProblemDetailIds: number[] = []
  myDbProblemDetails: MyDbProblemDetail[] | null = null

  // 서버 필터는 변형마다 필드가 다르고 비운 조건을 null 로 보낸다. 받은 키만 기본값 위에 덮어쓴다
  constructor(data?: Partial<MakeWorksheetFilterOption> | WorksheetFilterDraft) {
    super()

    if (data) {
      Object.assign(this, {
        ...data,
        excludedTopicIds: ('excludedTopicIds' in data ? data.excludedTopicIds : undefined) ?? [],
        excludedSubTopicIds:
          ('excludedSubTopicIds' in data ? data.excludedSubTopicIds : undefined) ?? [],
      })
    }

    makeObservable(this, {
      filterId: observable,
      practiceTest: observable,
      onlyAutoScorable: observable,
      excludePrevious: observable,
      previousExclusionScope: observable,
      studentIds: observable,
      problemFilterType: observable,
      excludeOOC: observable,
      includeMyDbProblem: observable,

      conceptIdList: observable,
      excludedTopicIds: observable,
      excludedSubTopicIds: observable,
      problemCount: observable,
      level: observable,
      levelWeight: observable,
      arithmeticOnly: observable,
      arithmeticLevel: observable,
      equalityLevel: observable,
      minRate: observable,
      maxRate: observable,

      problemOption: observable,
      pairX: observable,
      similarX: observable,
      similarLevel: observable,

      practiceProblemIdList: observable,
      curriculumIdList: observable,
      schoolGradeType: observable,
      year: observable,
      month: observable,
      department: observable,
      subjectList: observable,
      scoreList: observable,
      revision: observable,

      selectedConceptIdList: observable,
      selectedLittleChapterIdList: observable,

      startDate: observable,
      endDate: observable,
      source: observable,

      paperId: observable,
      paperTitle: observable,
      selectedPageIndexes: observable,
      schoolName: observable,
      schoolTestYear: observable,
      schoolTestType: observable,
      trieKey: observable,
      originWorkbook: observable,
      myDbProblemDetailIds: observable,
    })
  }

  get filterIdParams() {
    switch (this.type) {
      case 'PRACTICE_TEST':
        return this.getPracticeFilterParams()
      case 'PUBLIC_WORKBOOK':
        return this.getPublicWorkbookFilterParams()
      case 'WEAK_PERIOD':
        return this.getWeakPeriodFilterParams()
      case 'SCHOOL_TEST':
        return this.getSchoolTestFilterParams()
      case 'SCHOOL_TEST_PAPER':
        return this.getSchoolTestPaperFilterParams()
      case 'CUSTOM_PAPER':
        return this.getCustomPaperFilterParams()
      case 'ONE_CLICK':
        return this.getOneClickFilterParams()
      case 'MERGED_WORKSHEET':
        return this.getMergedWorksheetFilterParams()
      case 'MY_DB_ORIGINAL':
        return this.getMyDbOriginalFilterParams()
      case 'MY_DB_SIMILAR':
        return this.getMyDbSimilarFilterParams()
      default:
        // 'CONCEPT','WEAK_CONCEPT','WEAK_WORKSHEET'
        // weak-concept 엔드포인트는 연산 shape 를 받지 않는다
        return this.arithmeticOnly && this.type !== 'WEAK_CONCEPT'
          ? this.getArithmeticFilterParams()
          : this.getConceptFilterParams()
    }
  }

  // 기존 출제 문제 제외 조건은 excludePrevious + scope 가 함께 설정됐을 때만, studentIds 는 STUDENT_ASSIGNED 일 때만 전달한다
  private get previousExclusionParams(): Pick<
    ConceptWorksheetFilterBody,
    'previousExclusionScope' | 'studentIds'
  > {
    if (!this.excludePrevious || !this.previousExclusionScope) return {}
    return {
      previousExclusionScope: this.previousExclusionScope,
      ...(this.previousExclusionScope === 'STUDENT_ASSIGNED' && this.studentIds?.length
        ? { studentIds: this.studentIds }
        : {}),
    }
  }

  private getConceptFilterParams(): Omit<LeveledChapterFilterParams, 'problemList'> & {
    title: string
  } {
    return {
      filterId: this.filterId,
      conceptIdList: this.conceptIdList,
      excludedTopicIds: this.excludedTopicIds,
      excludedSubTopicIds: this.excludedSubTopicIds,
      problemCount: this.problemCount,
      level: this.level,
      levelWeight: this.levelWeight,
      problemFilterType: this.problemFilterType,
      practiceTest: this.practiceTest,
      onlyAutoScorable: this.onlyAutoScorable,
      excludePrevious: this.excludePrevious,
      excludeOOC: this.excludeOOC,
      includeMyDbProblem: this.includeMyDbProblem,
      equalityLevel: this.equalityLevel,
      minRate: this.minRate,
      maxRate: this.maxRate,
      title: this.title,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
      ...this.previousExclusionParams,
    }
  }

  // 연산 재추출은 level·levelWeight·problemFilterType·practiceTest·includeMyDbProblem 을 보내지 않는다 (서버가 채움)
  private getArithmeticFilterParams(): Omit<ArithmeticChapterFilterParams, 'problemList'> & {
    title: string
  } {
    return {
      filterId: this.filterId,
      arithmeticOnly: true,
      // 되읽기 null 대비 폴백 — 서버 기본값과 동일한 '전체'
      arithmeticLevel: this.arithmeticLevel ?? 'ALL',
      conceptIdList: this.conceptIdList,
      excludedTopicIds: this.excludedTopicIds,
      excludedSubTopicIds: this.excludedSubTopicIds,
      problemCount: this.problemCount,
      onlyAutoScorable: this.onlyAutoScorable,
      excludePrevious: this.excludePrevious,
      excludeOOC: this.excludeOOC,
      equalityLevel: this.equalityLevel,
      minRate: this.minRate,
      maxRate: this.maxRate,
      title: this.title,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
      ...this.previousExclusionParams,
    }
  }

  private getPublicWorkbookFilterParams(): Omit<
    PublicWorkbookWorksheetFilterBody,
    'problemList' | 'exampleProblemIds'
  > {
    return {
      filterId: this.filterId,
      workbookId: this.workbookId,
      problemFilterType: this.problemFilterType,
      workbookProblemIdList: this.workbookProblemIdList,
      practiceTest: this.practiceTest,
      onlyAutoScorable: this.onlyAutoScorable,
      excludePrevious: this.excludePrevious,
      problemOption: this.problemOption,
      pairX: this.pairX,
      similarX: this.similarX,
      excludeOOC: this.excludeOOC,
      similarLevel: this.similarLevel,
      limitProblemCountPerConcept: this.limitProblemCountPerConcept,
      title: this.title,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
      ...this.previousExclusionParams,
    }
  }

  private getPracticeFilterParams(): Omit<PracticeTestWorksheetFilterBody, 'problemList'> & {
    title: string
  } {
    return {
      filterId: this.filterId,
      practiceProblemIdList: this.practiceProblemIdList,
      curriculumIdList: this.curriculumIdList,
      scoreList: this.scoreList,
      problemFilterType: this.problemFilterType,
      onlyAutoScorable: this.onlyAutoScorable,
      excludeOOC: this.excludeOOC,
      title: this.title,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
    }
  }

  private getWeakPeriodFilterParams(): Omit<LeveledChapterFilterParams, 'problemList'> &
    Pick<WeakPeriodWorksheetFilterBody, 'startDate' | 'endDate'> {
    return { ...this.getConceptFilterParams(), startDate: this.startDate, endDate: this.endDate }
  }

  private getSchoolTestFilterParams(): Omit<SchoolTestWorksheetFilterBody, 'problemList'> {
    return {
      conceptIdList: this.conceptIdList,
      excludeOOC: this.excludeOOC,
      excludePrevious: this.excludePrevious,
      filterId: this.filterId,
      limitProblemCountPerConcept: this.limitProblemCountPerConcept,
      onlyAutoScorable: this.onlyAutoScorable,
      pairX: this.pairX,
      practiceTest: this.practiceTest,
      problemFilterType: this.problemFilterType,
      title: this.title,
      similarX: this.similarX,
      similarLevel: this.similarLevel,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
      schoolCodes: this.schoolCodes,
      schoolTestGrade: this.schoolTestGrade,
      schoolTestSchoolType: this.schoolTestSchoolType,
      schoolTestRevision: this.schoolTestRevision,
      schoolTestSemester: this.schoolTestSemester,
      schoolTestType: this.schoolTestType,
      schoolTestYears: this.schoolTestYears,
      ...this.previousExclusionParams,
    }
  }

  private getCustomPaperFilterParams(): Omit<CustomPaperWorksheetFilterBody, 'problemList'> {
    return {
      filterId: this.filterId,
      paperId: this.paperId,
      selectedPageIndexes: this.selectedPageIndexes,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
    }
  }
  private getSchoolTestPaperFilterParams(): Omit<
    SchoolTestPaperWorksheetFilterBody,
    'problemList'
  > {
    return {
      filterId: this.filterId,
      paperId: this.paperId,
      selectedPageIndexes: this.selectedPageIndexes,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
    }
  }

  private getOneClickFilterParams(): Omit<OneClickWorksheetFilterBody, 'problemList'> {
    return {
      studentId: this.studentId,
      filterId: this.filterId,
      conceptIdList: this.conceptIdList,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
      problemSource: this.problemSource,
      problemRequestType: this.problemRequestType,
      onlyAutoScorable: this.onlyAutoScorable,
      excludeOOC: this.excludeOOC,
      problemFilterType: this.problemFilterType,
      excludePrevious: this.excludePrevious,
      problemCount: this.problemCount,
      levelType: this.levelType,
      practiceTest: this.practiceTest,
      title: this.title,
    }
  }

  private getMergedWorksheetFilterParams(): Omit<
    MergedWorksheetFilterBody,
    | 'problems'
    | 'excludeOOC'
    | 'problemList'
    | 'selectedConceptIdList'
    | 'selectedLittleChapterIdList'
    | 'problemFilterType'
  > & {
    filterId: string
  } {
    return {
      filterId: this.filterId,
      mergedWorksheetIds: this.mergedWorksheetIds,
      problemSortType: this.problemSortType,
      onlyAutoScorable: this.onlyAutoScorable,
      title: this.title,
    }
  }

  // type 은 화면 분기용이라 요청에는 싣지 않는다(commonWorksheetService.createFilterId 가 필드를 골라 보낸다)
  private getMyDbOriginalFilterParams(): Omit<MyDbOriginalWorksheetFilterBody, 'problemList'> & {
    type: 'MY_DB_ORIGINAL'
  } {
    return {
      type: 'MY_DB_ORIGINAL',
      filterId: this.filterId,
      title: this.title,
      myDbProblemDetailIds: this.myDbProblemDetailIds,
    }
  }

  private getMyDbSimilarFilterParams(): Omit<MyDbSimilarWorksheetFilterBody, 'problemList'> & {
    type: 'MY_DB_SIMILAR'
  } {
    return {
      type: 'MY_DB_SIMILAR',
      filterId: this.filterId,
      title: this.title,
      similarX: this.similarX,
      similarLevel: this.similarLevel,
      myDbProblemDetailIds: this.myDbProblemDetailIds,
    }
  }
}
